import React, { useState, useEffect } from 'react';
import { dataService } from '../../services/dataService';
import { Card } from '../Card';
import { User, Teacher, Student, School, District } from '../../data/types';
import PageContainer from '../layouts/PageContainer';

const SuperAdminUserManagementScreen: React.FC = () => {
  // Add user modal state
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUser, setNewUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    role: 'SuperAdmin',
    schoolId: '',
  });
  const [addUserError, setAddUserError] = useState('');

  // Helper: get school options for dropdown
  const getSchoolOptions = () => {
    return schools.map((school) => ({ value: school.id, label: school.name }));
  };

  // Helper: get school object by id
  const getSchoolById = (id: string) => schools.find((s) => s.id === id);

  // Helper: get school name for any user
  const getUserSchoolName = (user: User | Teacher | Student & { schoolId?: string }) => {
    if (user.role === 'SuperAdmin') return '—';
    if (user.role === 'SchoolAdmin') {
      if ('schoolId' in user && typeof user.schoolId === 'string' && user.schoolId) {
        const school = getSchoolById(user.schoolId);
        return school ? school.name : '—';
      }
      return '—';
    }
    if ((user.role === 'Teacher' || user.role === 'Student') && (user as Teacher | Student).school) {
      return (user as Teacher | Student).school.name;
    }
    return '—';
  };

  // Add user handler
  const handleAddUser = () => {
    // Only SuperAdmin and SchoolAdmin can be created
    if (newUser.role !== 'SuperAdmin' && newUser.role !== 'SchoolAdmin') {
      setAddUserError('Only Super Admin or School Admin can be added.');
      return;
    }
    // SchoolAdmin must have a school
    if (newUser.role === 'SchoolAdmin' && !newUser.schoolId) {
      setAddUserError('Please assign a school to the School Admin.');
      return;
    }
    // For extensibility: any non-SuperAdmin must have a school
    if (newUser.role !== 'SuperAdmin' && !newUser.schoolId) {
      setAddUserError('This user must be associated with a school.');
      return;
    }
    // Generate a mock user object
    const userToAdd: User & { schoolId?: string } = {
      id: `user-${Date.now()}`,
      username: newUser.username || `${newUser.firstName.toLowerCase()}.${newUser.lastName.toLowerCase()}`,
      firstName: newUser.firstName,
      lastName: newUser.lastName,
      email: newUser.email,
      role: newUser.role as any,
      avatar: '',
      ...(newUser.role === 'SchoolAdmin' && newUser.schoolId ? { schoolId: newUser.schoolId } : {}),
    };
    setUsers((prev) => [...prev, userToAdd]);
    setShowAddUserModal(false);
    setNewUser({ firstName: '', lastName: '', email: '', username: '', role: 'SuperAdmin', schoolId: '' });
    setAddUserError('');
  };


  // All hooks must be declared at the top level, before any returns
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [actionType, setActionType] = useState<'reset' | 'deactivate' | 'delete'>('reset');
  // Filter state hooks (must be here)
  const [districts, setDistricts] = useState<District[]>([]);
  const [schools, setSchools] = useState<School[]>([]);
  const [filterDistrict, setFilterDistrict] = useState<string>('all');
  const [filterSchool, setFilterSchool] = useState<string>('all');

  // Fetch districts and schools on mount
  useEffect(() => {
    const fetchFilters = async () => {
      const [districtsData, schoolsData] = await Promise.all([
        dataService.getDistricts(),
        dataService.getSchools()
      ]);
      setDistricts(districtsData);
      setSchools(schoolsData);
    };
    fetchFilters();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Use the dataService to get all users from the mock data
        const allUsers = await dataService.getAllUsers();
        setUsers(allUsers);
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleUserAction = (user: User, action: 'reset' | 'deactivate' | 'delete') => {
    setSelectedUser(user);
    setActionType(action);
    setShowConfirmModal(true);
  };

  const confirmAction = async () => {
    if (!selectedUser) return;
    
    try {
      // In a real implementation, this would call an API to perform the action
      // For now, we'll just simulate the action with a delay
      
      // Create an audit log for the action
      await dataService.createAuditLog({
        userId: 'superadmin-001',
        userName: 'Super Admin',
        userRole: 'SuperAdmin',
        action: actionType === 'reset' ? 'UPDATE' : actionType === 'deactivate' ? 'UPDATE' : 'DELETE',
        resource: 'User',
        resourceId: selectedUser.id,
        details: `${actionType === 'reset' ? 'Reset password for' : 
                  actionType === 'deactivate' ? 'Deactivated' : 'Deleted'} user: ${selectedUser.firstName} ${selectedUser.lastName} (${selectedUser.role})`,
        ipAddress: '192.168.1.100'
      });
      
      // Update the user list if needed
      if (actionType === 'delete') {
        setUsers(users.filter(u => u.id !== selectedUser.id));
      }
      
      // Close the modal
      setShowConfirmModal(false);
      setSelectedUser(null);
    } catch (error) {
      console.error(`Error ${actionType}ing user:`, error);
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'SuperAdmin':
        return 'bg-purple-100 text-purple-800';
      case 'SchoolAdmin':
        return 'bg-blue-100 text-blue-800';
      case 'Teacher':
        return 'bg-green-100 text-green-800';
      case 'Student':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }


  // Filtered list of schools by district
  const filteredSchools = filterDistrict === 'all'
    ? schools
    : schools.filter((school) => school.district === filterDistrict);

  // Extended filtering logic (declare only here, remove any duplicate earlier declaration)
  // Extended filtering logic (only declared here)
  const filteredUsers = users.filter(user => {
    // Filter by role
    if (filterRole !== 'all' && user.role !== filterRole) {
      return false;
    }
    // Filter by status
    if (filterStatus === 'inactive') {
      return false;
    }
    // Filter by district
    if (filterDistrict !== 'all') {
      if ((user.role === 'Teacher' || user.role === 'Student') && (user as Teacher | Student).school) {
        if ((user as Teacher | Student).school.district !== filterDistrict) {
          return false;
        }
      } else {
        return false;
      }
    }
    // Filter by school
    if (filterSchool !== 'all') {
      if ((user.role === 'Teacher' || user.role === 'Student') && (user as Teacher | Student).school) {
        if ((user as Teacher | Student).school.id !== filterSchool) {
          return false;
        }
      } else {
        return false;
      }
    }
    // Filter by search query
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      return (
        user.firstName.toLowerCase().includes(searchLower) ||
        user.lastName.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower) ||
        user.username.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  return (
    <PageContainer title="User Management" userRole="SuperAdmin">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">User Management</h1>
      {/* Filter Controls */}
      <div className="flex flex-wrap gap-4 mb-4 items-end">
        {/* District Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">District</label>
          <select
            className="block w-40 h-10 px-2 py-1 border border-gray-300 rounded text-sm"
            value={filterDistrict}
            onChange={e => {
              setFilterDistrict(e.target.value);
              setFilterSchool('all'); // Reset school filter when district changes
            }}
          >
            <option value="all">All Districts</option>
            {districts.map((district: any) => (
              <option key={district.id} value={district.id}>{district.name}</option>
            ))}
          </select>
        </div>
        {/* School Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">School</label>
          <select
            className="block w-40 h-10 px-2 py-1 border border-gray-300 rounded text-sm"
            value={filterSchool}
            onChange={e => setFilterSchool(e.target.value)}
          >
            <option value="all">All Schools</option>
            {filteredSchools.map((school: any) => (
              <option key={school.id} value={school.id}>{school.name}</option>
            ))}
          </select>
        </div>
        {/* Role Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
          <select
            className="block w-32 h-10 px-2 py-1 border border-gray-300 rounded text-sm"
            value={filterRole}
            onChange={e => setFilterRole(e.target.value)}
          >
            <option value="all">All Roles</option>
            <option value="SuperAdmin">Super Admin</option>
            <option value="SchoolAdmin">School Admin</option>
            <option value="Teacher">Teacher</option>
            <option value="Student">Student</option>
            <option value="Admin">Admin</option>
          </select>
        </div>
        {/* Status Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            className="block w-32 h-10 px-2 py-1 border border-gray-300 rounded text-sm"
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        {/* Search Box (existing) */}
        <div className="flex-1 min-w-[180px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
          <input
            type="text"
            className="block w-full h-10 px-2 py-1 border border-gray-300 rounded text-sm"
            placeholder="Search users..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
        {/* Add New User Button */}
        <div className="ml-auto">
          <button
            className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark flex items-center"
            onClick={() => setShowAddUserModal(true)}
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add New User
          </button>
        </div>
      </div>

      {/* User List */}
      <Card className="bg-white p-6 shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">Users</h2>
          <div className="text-sm text-gray-500">
            Showing {filteredUsers.length} of {users.length} users
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  School
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <img 
                          className="h-10 w-10 rounded-full" 
                          src={user.avatar || `https://placehold.co/100x100/4a89dc/ffffff?text=${user.firstName.charAt(0)}${user.lastName.charAt(0)}`} 
                          alt={`${user.firstName} ${user.lastName}`} 
                        />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="text-sm text-gray-500">
                          @{user.username}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{user.email}</div>
                    {user.role === 'Teacher' && (
                      <div className="text-xs text-gray-500">
                        {(user as Teacher).school.name}
                      </div>
                    )}
                    {user.role === 'Student' && (
                      <div className="text-xs text-gray-500">
                        Grade {(user as Student).grade} - {(user as Student).school.name}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeColor(user.role)}`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {getUserSchoolName(user)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      Active
                    </span>
                  </td>
                  {/* School column */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {user.role !== 'SuperAdmin' &&
                      ((user.role === 'Teacher' || user.role === 'Student') && (user as Teacher | Student).school && (user as Teacher | Student).school.name)
                      ? (user as Teacher | Student).school.name
                      : <span className="text-gray-400">—</span>
                    }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button 
                      className="text-primary hover:text-primary-dark mr-3"
                      onClick={() => handleUserAction(user, 'reset')}
                    >
                      Reset Password
                    </button>
                    <button 
                      className="text-yellow-600 hover:text-yellow-800 mr-3"
                      onClick={() => handleUserAction(user, 'deactivate')}
                    >
                      Deactivate
                    </button>
                    {user.role !== 'SuperAdmin' && (
                      <button 
                        className="text-red-600 hover:text-red-800"
                        onClick={() => handleUserAction(user, 'delete')}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredUsers.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500">No users found matching your criteria.</p>
          </div>
        )}
        
        {filteredUsers.length > 0 && (
          <div className="mt-4 flex justify-between items-center">
            <div className="text-sm text-gray-700">
              Showing <span className="font-medium">1</span> to <span className="font-medium">{Math.min(filteredUsers.length, 10)}</span> of{' '}
              <span className="font-medium">{filteredUsers.length}</span> results
            </div>
            <div className="flex space-x-2">
              <button className="px-3 py-1 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50" disabled>
                Previous
              </button>
              <button className="px-3 py-1 bg-primary text-white rounded text-sm hover:bg-primary-dark">
                Next
              </button>
            </div>
          </div>
        )}
      </Card>
      
      {/* Confirmation Modal */}
      {showConfirmModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              {actionType === 'reset' ? 'Reset Password' : 
               actionType === 'deactivate' ? 'Deactivate User' : 'Delete User'}
            </h3>
            <p className="text-gray-600 mb-6">
              {actionType === 'reset' ? 
                `Are you sure you want to reset the password for ${selectedUser.firstName} ${selectedUser.lastName}? They will receive an email with instructions to set a new password.` : 
               actionType === 'deactivate' ? 
                `Are you sure you want to deactivate ${selectedUser.firstName} ${selectedUser.lastName}? They will no longer be able to access the platform.` : 
                `Are you sure you want to delete ${selectedUser.firstName} ${selectedUser.lastName}? This action cannot be undone.`}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                onClick={() => setShowConfirmModal(false)}
              >
                Cancel
              </button>
              <button
                className={`px-4 py-2 text-white rounded ${
                  actionType === 'reset' ? 'bg-blue-600 hover:bg-blue-700' : 
                  actionType === 'deactivate' ? 'bg-yellow-600 hover:bg-yellow-700' : 
                  'bg-red-600 hover:bg-red-700'
                }`}
                onClick={confirmAction}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    {/* Add User Modal */}
    {showAddUserModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Add New User</h3>
          {addUserError && (
            <div className="mb-2 p-2 bg-red-100 text-red-700 rounded">{addUserError}</div>
          )}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                value={newUser.firstName}
                onChange={e => setNewUser({ ...newUser, firstName: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                value={newUser.lastName}
                onChange={e => setNewUser({ ...newUser, lastName: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                value={newUser.email}
                onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                value={newUser.username}
                onChange={e => setNewUser({ ...newUser, username: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                value={newUser.role}
                onChange={e => setNewUser({ ...newUser, role: e.target.value as any, schoolId: '' })}
              >
                <option value="SuperAdmin">Super Admin</option>
                <option value="SchoolAdmin">School Admin</option>
              </select>
            </div>
            {newUser.role === 'SchoolAdmin' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assign to School</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                  value={newUser.schoolId}
                  onChange={e => setNewUser({ ...newUser, schoolId: e.target.value })}
                  required
                >
                  <option value="">Select a school</option>
                  {getSchoolOptions().map(school => (
                    <option key={school.value} value={school.value}>{school.label}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="flex justify-end space-x-3 mt-6">
            <button
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              onClick={() => { setShowAddUserModal(false); setAddUserError(''); }}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark"
              onClick={handleAddUser}
            >
              Add User
            </button>
          </div>
        </div>
      </div>
    )}
  </PageContainer>
  );
};

export default SuperAdminUserManagementScreen;
